// Познатият телефон в „Нова резервация" — чиста логика без React (тестове:
// guestHistory.test.ts): кога формата попълва името от последната резервация
// и как се чете „последно идване". Данните идват готови от BE
// (`GET /reservations/guest-history`, същите правила като справка „Гости").

import type { GuestHistory } from "../../../api/reservations";
import { parseDateKey, toDateKey } from "./dayTime";

const DAY_MS = 86_400_000;
const DAYS_IN_WEEK = 7;
const DAYS_IN_MONTH = 30;
const DAYS_IN_YEAR = 365;

/** Отговорът за ТЕКУЩИЯ телефон във формата; `history` — null при непознат номер. */
export interface GuestLookupAnswer {
  phone: string;
  history: GuestHistory | null;
}

/** Познат е номер с поне една резервация (без редактираната). */
export const knownGuestOf = (history: GuestHistory | undefined): GuestHistory | null =>
  history && history.reservations > 0 ? history : null;

/** Гостът има отказани или неизпълнени резервации — точката на чипа. */
export const hasMissedReservations = (history: GuestHistory): boolean =>
  history.cancelled + history.expired > 0;

/**
 * Името след отговора за телефона; `null` — полето остава както е.
 * Попълва се само ПРАЗНО поле или поле, в което още стои предишното
 * попълване — писаното от човек не се пипа никога. Непознат номер изтрива
 * предишното попълване: то е името на друг телефон.
 */
export const autofilledGuestName = ({
  currentName,
  autofilledName,
  knownName,
}: {
  currentName: string;
  // Последното попълнено от формата име (null — още няма).
  autofilledName: string | null;
  // Името от последната резервация на телефона (null — непознат номер).
  knownName: string | null;
}): string | null => {
  const holdsAutofill = autofilledName !== null && currentName === autofilledName;
  if (holdsAutofill) {
    const nextName = knownName ?? "";
    return nextName === currentName ? null : nextName;
  }
  return currentName.trim() === "" && knownName ? knownName : null;
};

/** Колко календарни дни (на терминала) от `iso` до `nowMs`; бъдещ момент → 0. */
export const calendarDaysSince = (iso: string, nowMs: number): number => {
  const fromMidnight = parseDateKey(toDateKey(new Date(iso))).getTime();
  const toMidnight = parseDateKey(toDateKey(new Date(nowMs))).getTime();
  // round: денят при смяна на лятно/зимно време е 23 или 25 часа.
  return Math.max(0, Math.round((toMidnight - fromMidnight) / DAY_MS));
};

/**
 * „днес" / „вчера" / „преди 5 дни" / „преди 2 седмици" / „преди 3 месеца" /
 * „преди 1 година" — на езика на интерфейса (`Intl.RelativeTimeFormat`).
 */
export const formatDaysAgo = (days: number, locale: string): string => {
  if (days < DAYS_IN_WEEK) {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(-days, "day");
  }
  // „always": „преди 1 седмица", не календарното „миналата седмица".
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: "always" });
  if (days < DAYS_IN_MONTH) return relative.format(-Math.floor(days / DAYS_IN_WEEK), "week");
  if (days < DAYS_IN_YEAR) return relative.format(-Math.floor(days / DAYS_IN_MONTH), "month");
  return relative.format(-Math.floor(days / DAYS_IN_YEAR), "year");
};

/** „12.09.2026" — денят на момента на терминала. */
export const formatCalendarDate = (iso: string): string => {
  const [year, month, day] = toDateKey(new Date(iso)).split("-");
  return `${day}.${month}.${year}`;
};
